import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import ProjectPreview from '../components/ProjectPreview';
import CTA from '../components/CTA';
import NotFound from './NotFound';
import { getProject } from '../data/portfolio';
import './PortfolioItem.css';

const PortfolioItem = () => {
  const { id } = useParams();
  const item = getProject(id);

  if (!item) {
    return (
      <NotFound
        title="Project not found"
        message="The portfolio item you're looking for doesn't exist."
        backTo="/portfolio"
        backLabel="Back to portfolio"
      />
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: item.title,
    description: item.description,
    url: item.url,
    dateCreated: item.date,
    creator: {
      '@type': 'Organization',
      name: 'ESCR LLC'
    },
    about: {
      '@type': 'Thing',
      name: item.category
    }
  };

  const details = [
    { label: 'Client', value: item.client },
    { label: 'Category', value: item.category },
    { label: 'Year', value: item.date },
    { label: 'Type', value: item.projectType },
  ];

  return (
    <div className="portfolio-item">
      <SEO
        title={item.title}
        description={item.description}
        keywords={`${item.category}, portfolio, ${item.technologies.join(', ')}, ${item.title}`}
        structuredData={structuredData}
        canonicalUrl={`https://escr.online/portfolio/${item.id}`}
      />

      <header className="case-hero">
        <div className="grid-bg" aria-hidden="true" />
        <div className="glow case-glow" aria-hidden="true" />
        <div className="container case-hero-inner">
          <Link to="/portfolio" className="back-link case-back">
            <Icon name="arrowLeft" />
            All projects
          </Link>
          <span className="eyebrow">Case study</span>
          <h1 className="case-title">{item.title}</h1>
          <p className="case-subtitle">{item.description}</p>

          <dl className="case-details">
            {details.map((d) => (
              <div key={d.label} className="case-detail">
                <dt>{d.label}</dt>
                <dd>{d.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </header>

      <section className="case-visual-section">
        <div className="container">
          <Reveal className="case-visual">
            <ProjectPreview project={item} size="lg" />
          </Reveal>
        </div>
      </section>

      <section className="section section-tight">
        <div className="container case-content">
          <div className="prose" dangerouslySetInnerHTML={{ __html: item.fullDescription }} />

          <aside className="case-sidebar">
            <div className="card case-card">
              <h2 className="case-card-title">Technologies</h2>
              <div className="case-tech">
                {item.technologies.map((tech) => (
                  <span key={tech} className="tag">{tech}</span>
                ))}
              </div>
            </div>
            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-large case-visit"
                aria-label={`Visit ${item.title} website (opens in a new tab)`}
              >
                Visit live site
                <Icon name="arrowUpRight" />
              </a>
            )}
          </aside>
        </div>
      </section>

      <CTA title="Want results like these?" secondary={{ to: '/portfolio', label: 'All projects' }} />
    </div>
  );
};

export default PortfolioItem;
